import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgencyContact() {
  const { agency } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/enquiries/${agency.slug}`, formData);
      toast.success('Message sent! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFDFF]" data-testid="agency-contact">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-20">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-sky-500 font-bold uppercase tracking-widest text-sm mb-6 inline-block">Connect With Us</span>
            <h1 className="text-5xl sm:text-6xl font-heading font-bold text-navy-900 mb-8">
              Request a <br />
              <span className="text-sky-500 italic">Private Viewing.</span>
            </h1>
            <p className="text-lg text-navy-500 mb-12 font-light leading-relaxed max-w-md italic">
              Your journey to an exceptional lifestyle begins with a single conversation. Our dedicated concierge team awaits your inquiry.
            </p>

            <div className="space-y-8">
              <div className="flex items-center gap-6 group">
                <div className="w-14 h-14 bg-white shadow-lg ring-1 ring-navy-50 rounded-2xl flex items-center justify-center group-hover:bg-sky-50 transition-all">
                  <Phone className="w-6 h-6 text-sky-500" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-navy-400 font-bold mb-1">Call Concierge</p>
                  <p className="text-xl font-medium text-navy-900">{agency.phone}</p>
                </div>
              </div>

              <div className="flex items-center gap-6 group">
                <div className="w-14 h-14 bg-white shadow-lg ring-1 ring-navy-50 rounded-2xl flex items-center justify-center group-hover:bg-sky-50 transition-all">
                  <Mail className="w-6 h-6 text-sky-500" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-navy-400 font-bold mb-1">Email Inquiry</p>
                  <p className="text-xl font-medium text-navy-900">{agency.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-6 group">
                <div className="w-14 h-14 bg-white shadow-lg ring-1 ring-navy-50 rounded-2xl flex items-center justify-center group-hover:bg-sky-50 transition-all">
                  <Clock className="w-6 h-6 text-sky-500" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-navy-400 font-bold mb-1">Business Hours</p>
                  <p className="text-xl font-medium text-navy-900">Mon - Sat: 9:00 AM - 7:00 PM</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="glass p-10 rounded-[2.5rem] border border-white shadow-2xl"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-navy-900 font-bold ml-1">Your Name</Label>
                  <Input
                    placeholder="John Doe"
                    className="h-14 bg-white/50 border-navy-50 rounded-2xl focus:ring-sky-500"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-navy-900 font-bold ml-1">Phone Number</Label>
                  <Input
                    placeholder="+91 00000 00000"
                    className="h-14 bg-white/50 border-navy-50 rounded-2xl focus:ring-sky-500"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-navy-900 font-bold ml-1">Email Address</Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  className="h-14 bg-white/50 border-navy-50 rounded-2xl focus:ring-sky-500"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-navy-900 font-bold ml-1">Message</Label>
                <Textarea
                  placeholder="Tell us about your property requirements..."
                  className="min-h-[150px] bg-white/50 border-navy-50 rounded-2xl focus:ring-sky-500 p-4"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                />
              </div>
              <Button disabled={loading} className="w-full bg-navy-900 hover:bg-navy-800 text-white h-16 rounded-2xl text-lg font-heading shadow-xl shadow-navy-200">
                {loading ? 'Sending...' : 'Send Inquiry'}
              </Button>
            </form>

            {/* WhatsApp Alternative */}
            <div className="mt-8 pt-8 border-t border-navy-100 flex flex-col items-center">
              <p className="text-sm text-navy-400 mb-4">Prefer instant communication?</p>
              <a
                href={`https://wa.me/${agency.phone.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-green-600 font-bold hover:scale-105 transition-transform"
              >
                <MessageCircle className="w-5 h-5 fill-current" />
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
